import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export interface BedAvailability {
  service: string
  serviceLabel: string
  total: number
  available: number
  occupied: number
  reserved: number
}

export interface SpecialistGuard {
  specialty: string
  specialtyLabel: string
  name: string
  isOnCall: boolean
}

export interface BloodStock {
  group: string
  quantity: number
  unit: string
  status: 'normal' | 'alert' | 'critical'
}

export interface MedicationStock {
  name: string
  nameLabel: string
  quantity: number
  unit: string
  status: 'normal' | 'alert' | 'critical'
}

interface AvailabilityState {
  structureId: string
  structureName: string
  beds: BedAvailability[]
  specialists: SpecialistGuard[]
  bloodStocks: BloodStock[]
  medications: MedicationStock[]
  ambulances: { smur: number; transport: number }
  lastUpdated: string | null
  lastSyncTime: string | null
  isDirty: boolean

  setStructure: (id: string, name: string) => void
  updateBed: (service: string, field: 'available' | 'occupied' | 'reserved', value: number) => void
  toggleSpecialist: (specialty: string) => void
  updateSpecialistName: (specialty: string, name: string) => void
  updateBloodStock: (group: string, quantity: number) => void
  updateMedication: (name: string, quantity: number) => void
  updateAmbulances: (type: 'smur' | 'transport', count: number) => void
  markSaved: () => void
  setLastSyncTime: (time: string) => void
  resetAll: () => void
}

const DEFAULT_BEDS: BedAvailability[] = [
  { service: 'urgences', serviceLabel: 'Urgences', total: 20, available: 0, occupied: 0, reserved: 0 },
  { service: 'reanimation', serviceLabel: 'Réanimation / USI', total: 8, available: 0, occupied: 0, reserved: 0 },
  { service: 'medecine', serviceLabel: 'Médecine générale', total: 30, available: 0, occupied: 0, reserved: 0 },
  { service: 'chirurgie', serviceLabel: 'Chirurgie', total: 20, available: 0, occupied: 0, reserved: 0 },
  { service: 'maternite', serviceLabel: 'Maternité', total: 15, available: 0, occupied: 0, reserved: 0 },
  { service: 'pediatrie', serviceLabel: 'Pédiatrie', total: 12, available: 0, occupied: 0, reserved: 0 },
]

const DEFAULT_SPECIALISTS: SpecialistGuard[] = [
  { specialty: 'urgentiste', specialtyLabel: 'Urgentiste', name: '', isOnCall: false },
  { specialty: 'chirurgien', specialtyLabel: 'Chirurgien', name: '', isOnCall: false },
  { specialty: 'obstetricien', specialtyLabel: 'Obstétricien', name: '', isOnCall: false },
  { specialty: 'anesthesiste', specialtyLabel: 'Anesthésiste', name: '', isOnCall: false },
  { specialty: 'cardiologue', specialtyLabel: 'Cardiologue', name: '', isOnCall: false },
  { specialty: 'pediatre', specialtyLabel: 'Pédiatre', name: '', isOnCall: false },
  { specialty: 'radiologue', specialtyLabel: 'Radiologue', name: '', isOnCall: false },
]

const DEFAULT_BLOOD: BloodStock[] = [
  { group: 'O+', quantity: 0, unit: 'poches', status: 'normal' },
  { group: 'O-', quantity: 0, unit: 'poches', status: 'normal' },
  { group: 'A+', quantity: 0, unit: 'poches', status: 'normal' },
  { group: 'A-', quantity: 0, unit: 'poches', status: 'normal' },
  { group: 'B+', quantity: 0, unit: 'poches', status: 'normal' },
  { group: 'B-', quantity: 0, unit: 'poches', status: 'normal' },
  { group: 'AB+', quantity: 0, unit: 'poches', status: 'normal' },
  { group: 'AB-', quantity: 0, unit: 'poches', status: 'normal' },
]

const DEFAULT_MEDICATIONS: MedicationStock[] = [
  { name: 'adrenaline', nameLabel: 'Adrénaline', quantity: 0, unit: 'ampoules', status: 'normal' },
  { name: 'oxytocine', nameLabel: 'Oxytocine', quantity: 0, unit: 'ampoules', status: 'normal' },
  { name: 'mgso4', nameLabel: 'Sulfate de Magnésium', quantity: 0, unit: 'ampoules', status: 'normal' },
  { name: 'antivenin', nameLabel: 'Antivenin', quantity: 0, unit: 'flacons', status: 'normal' },
]

const useAvailabilityStore = create<AvailabilityState>()(
  persist(
    (set) => ({
      structureId: '',
      structureName: '',
      beds: DEFAULT_BEDS,
      specialists: DEFAULT_SPECIALISTS,
      bloodStocks: DEFAULT_BLOOD,
      medications: DEFAULT_MEDICATIONS,
      ambulances: { smur: 0, transport: 0 },
      lastUpdated: null,
      lastSyncTime: null,
      isDirty: false,

      setStructure: (id, name) => set({ structureId: id, structureName: name }),

      updateBed: (service, field, value) =>
        set((s) => ({
          beds: s.beds.map((b) => (b.service === service ? { ...b, [field]: Math.max(0, value) } : b)),
          isDirty: true,
        })),

      toggleSpecialist: (specialty) =>
        set((s) => ({
          specialists: s.specialists.map((sp) =>
            sp.specialty === specialty ? { ...sp, isOnCall: !sp.isOnCall } : sp
          ),
          isDirty: true,
        })),

      updateSpecialistName: (specialty, name) =>
        set((s) => ({
          specialists: s.specialists.map((sp) =>
            sp.specialty === specialty ? { ...sp, name } : sp
          ),
          isDirty: true,
        })),

      updateBloodStock: (group, quantity) =>
        set((s) => ({
          bloodStocks: s.bloodStocks.map((b) =>
            b.group === group
              ? { ...b, quantity: Math.max(0, quantity), status: quantity <= 0 ? 'critical' : quantity <= 2 ? 'alert' : 'normal' }
              : b
          ),
          isDirty: true,
        })),

      updateMedication: (name, quantity) =>
        set((s) => ({
          medications: s.medications.map((m) =>
            m.name === name
              ? { ...m, quantity: Math.max(0, quantity), status: quantity <= 0 ? 'critical' : quantity <= 3 ? 'alert' : 'normal' }
              : m
          ),
          isDirty: true,
        })),

      updateAmbulances: (type, count) =>
        set((s) => ({
          ambulances: { ...s.ambulances, [type]: Math.max(0, count) },
          isDirty: true,
        })),

      markSaved: () => set({ isDirty: false, lastUpdated: new Date().toISOString() }),

      setLastSyncTime: (time) => set({ lastSyncTime: time }),

      resetAll: () =>
        set({
          beds: DEFAULT_BEDS,
          specialists: DEFAULT_SPECIALISTS,
          bloodStocks: DEFAULT_BLOOD,
          medications: DEFAULT_MEDICATIONS,
          ambulances: { smur: 0, transport: 0 },
          isDirty: false,
        }),
    }),
    { name: 'sante-availability' }
  )
)

export default useAvailabilityStore
